"use client";
import ImageWithSkeleton from "@/components/ui/ImageWithSkeleton";
import { IFile } from "@/types";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";

interface MarkdownRendererProps {
    content: string;
    images?: IFile[];
    className?: string;
}

export default function MarkdownRenderer({
    content,
    images = [],
    className = "",
}: MarkdownRendererProps) {
    // Process content to replace {{image1}}, {{image2}}, etc. with standard markdown images using direct public URLs
    const processedContent = (content || "").replace(
        /\{\{image(\d+)\}\}/gi,
        (_match, p1) => {
            const index = parseInt(p1, 10) - 1;
            const targetImg = images[index];
            return `![${targetImg?.name || `Case Study Image ${p1}`}](${targetImg?.cloudinaryUrl || ""})`;
        },
    );

    return (
        <div
            className={`markdown-body space-y-4 leading-relaxed text-text-color ${className}`}
        >
            <ReactMarkdown
                remarkPlugins={[remarkGfm]}
                components={{
                    // Headers
                    h1: ({ children }) => (
                        <h1 className="my-6 text-2xl font-black text-primary sm:text-3xl">
                            {children}
                        </h1>
                    ),
                    h2: ({ children }) => (
                        <h2 className="my-5 text-xl font-bold text-primary sm:text-2xl">
                            {children}
                        </h2>
                    ),
                    h3: ({ children }) => (
                        <h3 className="my-4 text-lg font-bold text-primary sm:text-xl">
                            {children}
                        </h3>
                    ),
                    h4: ({ children }) => (
                        <h4 className="my-3 text-base font-bold text-primary">
                            {children}
                        </h4>
                    ),

                    // Paragraphs
                    p: ({ children }) => (
                        <p className="text-text-color/90 my-3 text-sm leading-relaxed sm:text-base">
                            {children}
                        </p>
                    ),

                    // Divider / Horizontal Rule (---, ***, ___)
                    hr: () => (
                        <hr className="my-8 origin-center border-t-2 border-border-color" />
                    ),

                    // Emphasis & Strong
                    strong: ({ children }) => (
                        <strong className="font-bold text-primary">
                            {children}
                        </strong>
                    ),
                    em: ({ children }) => (
                        <em className="italic text-text-color">{children}</em>
                    ),
                    del: ({ children }) => (
                        <del className="text-muted-foreground line-through">
                            {children}
                        </del>
                    ),

                    // Blockquotes & Callouts (> Quote)
                    blockquote: ({ children }) => (
                        <blockquote className="my-4 rounded-xl border-l-4 border-primary bg-primary/10 p-4 text-sm italic text-text-color shadow-sm">
                            {children}
                        </blockquote>
                    ),

                    // Lists & Tasks
                    ul: ({ children }) => (
                        <ul className="my-3 ml-6 list-disc space-y-1 text-sm text-text-color">
                            {children}
                        </ul>
                    ),
                    ol: ({ children }) => (
                        <ol className="my-3 ml-6 list-decimal space-y-1 text-sm text-text-color">
                            {children}
                        </ol>
                    ),
                    li: ({ children }) => (
                        <li className="leading-relaxed">{children}</li>
                    ),

                    // Tables
                    table: ({ children }) => (
                        <div className="my-6 overflow-x-auto rounded-xl border border-border-color shadow-sm">
                            <table className="w-full border-collapse text-left text-sm text-text-color">
                                {children}
                            </table>
                        </div>
                    ),
                    thead: ({ children }) => (
                        <thead className="border-b border-border-color bg-primary/10 text-xs font-bold uppercase text-primary">
                            {children}
                        </thead>
                    ),
                    tbody: ({ children }) => (
                        <tbody className="bg-card-bg/40 divide-y divide-border-color">
                            {children}
                        </tbody>
                    ),
                    tr: ({ children }) => (
                        <tr className="hover:bg-card-bg/80 transition">
                            {children}
                        </tr>
                    ),
                    th: ({ children }) => (
                        <th className="px-4 py-3 font-semibold">{children}</th>
                    ),
                    td: ({ children }) => (
                        <td className="px-4 py-3">{children}</td>
                    ),

                    // Links
                    a: ({ href, children }) => (
                        <a
                            href={href}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="font-medium text-primary underline underline-offset-4 transition hover:opacity-80"
                        >
                            {children}
                        </a>
                    ),

                    // Code Blocks & Inline Code
                    code: ({ className: codeClassName, children }) => {
                        const isInline = !codeClassName;
                        if (isInline) {
                            return (
                                <code className="rounded-md border border-border-color bg-card-bg px-1.5 py-0.5 font-mono text-xs font-semibold text-primary">
                                    {children}
                                </code>
                            );
                        }
                        return (
                            <div className="my-4 overflow-hidden rounded-xl border border-border-color bg-zinc-100 shadow-sm dark:bg-zinc-950">
                                <pre className="overflow-x-auto p-4 font-mono text-xs leading-relaxed text-zinc-800 sm:text-sm dark:text-emerald-400">
                                    <code>{children}</code>
                                </pre>
                            </div>
                        );
                    },

                    // Images (Standard Markdown & {{imageX}} resolved)
                    img: ({ src, alt }) => {
                        if (!src) return null;
                        return (
                            <div className="group my-6 overflow-hidden rounded-2xl border border-border-color shadow-md">
                                <div className="bg-card-bg/60 relative aspect-video w-full overflow-hidden">
                                    <ImageWithSkeleton
                                        src={src as string}
                                        alt={
                                            (alt as string) ||
                                            "Case Study Image"
                                        }
                                        isLoadingUrl={!src}
                                        fill
                                        sizes="(max-width: 1024px) 100vw, 800px"
                                        containerClassName="relative h-full w-full"
                                        className="object-cover transition-transform duration-700 ease-out group-hover:scale-[1.03]"
                                    />
                                </div>
                            </div>
                        );
                    },
                }}
            >
                {processedContent}
            </ReactMarkdown>
        </div>
    );
}
